import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { SavedItem, ItemInput, ItemResult } from '@creanizer/types'
import { createIndexedDBStorage } from '@creanizer/utils'

interface ItemStore {
  items: SavedItem[]
  addItem: (item: Omit<SavedItem, 'id' | 'createdAt' | 'updatedAt'>) => void
  addBatchItems: (items: Omit<SavedItem, 'id' | 'createdAt' | 'updatedAt'>[]) => void
  updateItem: (id: string, item: Partial<SavedItem>) => void
  removeItem: (id: string) => void
  getItem: (id: string) => SavedItem | undefined
  toggleFavorite: (id: string) => void
  exportAsText: () => void
  exportAsJSON: () => void
  exportSingleAsText: (id: string) => void
  exportSingleAsJSON: (id: string) => void
  importFromJSON: (jsonString: string) => void
  fixDuplicateIds: () => boolean
  clearAll: () => void
  clearItems: () => void
  getItemsByWorld: (worldId: string) => SavedItem[]
}

const MAX_ITEMS = 500

export const useItemStore = create<ItemStore>()(
  persist(
    (set, get) => ({
      items: [],

      addItem: (item) => {
        const newItem: SavedItem = {
          ...item,
          id: `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }

        set(state => {
          const items = [newItem, ...state.items]
          if (items.length > MAX_ITEMS) {
            items.pop()
          }
          return { items }
        })
      },
      
      addBatchItems: (batchItems) => {
        const newItems: SavedItem[] = batchItems.map((item, index) => ({
          ...item,
          // バッチ保存時のID重複防止（50ms遅延相当の処理）
          id: `${Date.now() + index}_${Math.random().toString(36).slice(2, 11)}`,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }))

        set(state => {
          let items = [...newItems, ...state.items]
          if (items.length > MAX_ITEMS) {
            items = items.slice(0, MAX_ITEMS)
          }
          return { items }
        })
      },
      
      // ID重複をチェックして修正する関数
      fixDuplicateIds: () => {
        const { items } = get()
        const idMap = new Map<string, number>()
        const duplicates: string[] = []
        
        // 重複IDを検出
        items.forEach(item => {
          const count = idMap.get(item.id) || 0
          idMap.set(item.id, count + 1)
          if (count > 0) {
            duplicates.push(item.id)
          }
        })
        
        if (duplicates.length === 0) {
          return false
        }
        
        // 重複IDを修正
        set(state => ({
          items: state.items.map(item => {
            if (duplicates.includes(item.id)) {
              return {
                ...item,
                id: `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
                updatedAt: new Date().toISOString()
              }
            }
            return item
          })
        }))
        
        return true
      },

      updateItem: (id, updates) => {
        set(state => ({
          items: state.items.map(item =>
            item.id === id
              ? { ...item, ...updates, updatedAt: new Date().toISOString() }
              : item
          )
        }))
      },

      removeItem: (id) => {
        set(state => ({
          items: state.items.filter(item => item.id !== id)
        }))
      },

      getItem: (id) => {
        return get().items.find(item => item.id === id)
      },

      toggleFavorite: (id) => {
        set(state => ({
          items: state.items.map(item =>
            item.id === id
              ? { ...item, favorite: !item.favorite, updatedAt: new Date().toISOString() }
              : item
          )
        }))
      },
      
      getItemsByWorld: (worldId) => {
        return get().items.filter(item => item.worldId === worldId)
      },

      exportAsText: () => {
        const { items } = get()
        const text = items.map(item => {
          const result = item.result
          // originStoryに対応した新しい形式（型アサーションで互換性確保）
          const loreText = (result.lore as any)?.originStory?.purpose || (result.lore as any)?.origin?.purpose || ''
          const flavorText = result.lore?.flavorText || ''
          
          return `
■ ${result.basicInfo.name}
${result.basicInfo.summary}

【基本情報】
タイプ: ${result.basicInfo.type}
カテゴリ: ${result.basicInfo.category}
レア度: ${result.basicInfo.rarity}

【説明】
${result.basicInfo.description}

【外見】
${result.appearance.visualDescription}
素材: ${result.appearance.material}
サイズ: ${result.appearance.size}
重量: ${result.appearance.weight}

【効果】
${result.effects.primaryEffect.description}
効果の強さ: ${result.effects.primaryEffect.magnitude}
${result.effects.primaryEffect.duration ? `持続時間: ${result.effects.primaryEffect.duration}` : ''}

【入手方法】
${result.acquisition.obtainMethod.join('\n')}

【伝承】
${loreText}
${flavorText}

---`
        }).join('\n\n')

        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `items_${new Date().toISOString().split('T')[0]}.txt`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      },

      exportAsJSON: () => {
        const { items } = get()
        if (items.length === 0) {
          alert('保存されたアイテムがありません。')
          return
        }
        
        const exportData = {
          version: '1.0.0',
          type: 'items',
          exportDate: new Date().toISOString(),
          count: items.length,
          data: items
        }
        
        const json = JSON.stringify(exportData, null, 2)
        const blob = new Blob([json], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `saved_items_${Date.now()}.json`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      },
      
      exportSingleAsText: (id: string) => {
        const item = get().getItem(id)
        if (!item) {
          alert('アイテムが見つかりません。')
          return
        }
        
        const result = item.result
        const loreText = (result.lore as any)?.originStory?.purpose || (result.lore as any)?.origin?.purpose || ''
        const flavorText = result.lore?.flavorText || ''
        
        const text = `■ ${result.basicInfo.name}
${result.basicInfo.summary}

【基本情報】
タイプ: ${result.basicInfo.type}
カテゴリ: ${result.basicInfo.category}
レア度: ${result.basicInfo.rarity}

【説明】
${result.basicInfo.description}

【外見】
${result.appearance.visualDescription}
素材: ${result.appearance.material}
サイズ: ${result.appearance.size}
重量: ${result.appearance.weight}

【効果】
${result.effects.primaryEffect.description}
効果の強さ: ${result.effects.primaryEffect.magnitude}
${result.effects.primaryEffect.duration ? `持続時間: ${result.effects.primaryEffect.duration}` : ''}

【入手方法】
${result.acquisition.obtainMethod.join('\n')}

【伝承】
${loreText}
${flavorText}

作成日: ${new Date(item.createdAt).toLocaleDateString('ja-JP')}
${item.updatedAt ? `更新日: ${new Date(item.updatedAt).toLocaleDateString('ja-JP')}` : ''}
`
        
        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `item_${result.basicInfo.name}_${Date.now()}.txt`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      },
      
      exportSingleAsJSON: (id: string) => {
        const item = get().getItem(id)
        if (!item) {
          alert('アイテムが見つかりません。')
          return
        }
        
        const exportData = {
          version: '1.0.0',
          type: 'item',
          exportDate: new Date().toISOString(),
          data: item
        }
        
        const json = JSON.stringify(exportData, null, 2)
        const blob = new Blob([json], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `item_${item.result.basicInfo.name}_${Date.now()}.json`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      },

      importFromJSON: (jsonString) => {
        try {
          console.log('[ItemStore] importFromJSON開始')
          console.log('[ItemStore] 受信JSON長さ:', jsonString.length)
          const imported = JSON.parse(jsonString)
          console.log('[ItemStore] パース後データ:', imported)
          
          // 複数の形式をサポート
          let items: SavedItem[]
          if (imported.type === 'item' && imported.data && !Array.isArray(imported.data)) {
            // 単独アイテムの場合
            console.log('[ItemStore] 単独アイテム形式検出')
            items = [imported.data as SavedItem]
          } else {
            // 複数アイテムの場合
            items = (
              imported.data?.items ||  // VersionManager変換後の形式
              (imported.type === 'items' ? imported.data : null) ||  // 個別エクスポート形式
              imported.data ||  // BulkDataManager形式
              (Array.isArray(imported) ? imported : [])  // 直接配列形式
            ) as SavedItem[]
            console.log('[ItemStore] 複数アイテム形式検出:', items.length, '件')
          }
          const currentItems = get().items
          
          const validItems = items.filter(item => 
            item.result && 
            item.result.basicInfo && 
            item.result.appearance &&
            item.result.effects &&
            item.result.acquisition &&
            item.result.lore
          )
          
          // 重複チェック（タイトルとアイテム名で判定）
          const uniqueItems = validItems.filter(newItem => {
            return !currentItems.some(existing => 
              existing.title === newItem.title && 
              existing.result.basicInfo.name === newItem.result.basicInfo.name
            )
          })
          
          const processedItems = uniqueItems.map(item => {
            // 古い形式のデータを新しい形式に変換
            const migratedItem = { ...item }
            
            // 古いフィールドを削除（存在する場合）
            if (migratedItem.result.basicInfo) {
              delete (migratedItem.result.basicInfo as any).stackable
              delete (migratedItem.result.basicInfo as any).tradeable
              delete (migratedItem.result.basicInfo as any).consumable
              delete (migratedItem.result.basicInfo as any).questItem
            }
            
            // 古い価格フィールドを削除
            if (migratedItem.result.acquisition) {
              delete (migratedItem.result.acquisition as any).basePrice
              delete (migratedItem.result.acquisition as any).marketValue
              delete (migratedItem.result.acquisition as any).blackMarketValue
              delete (migratedItem.result.acquisition as any).crafting
              delete (migratedItem.result.acquisition as any).merchantLocations
              delete (migratedItem.result.acquisition as any).dropSources
              delete (migratedItem.result.acquisition as any).requiredCondition
            }
            
            // 古いstatsフィールドを削除
            if (migratedItem.result.effects) {
              delete (migratedItem.result.effects as any).stats
            }
            
            // loreの形式は両方サポート（originとoriginStory両方を保持）
            // エクスポート時に適切に処理されるため、ここでは変換しない
            
            return migratedItem
          })

          console.log('[ItemStore] 有効アイテム数:', validItems.length)
          console.log('[ItemStore] 重複を除いたアイテム数:', uniqueItems.length)
          
          // 重複がすべての場合でもエラーにしない（他のストアと統一）
          if (uniqueItems.length === 0 && validItems.length > 0) {
            console.log('[ItemStore] すべてのアイテムが重複のためスキップ')
            alert(`すべてのアイテムが既に存在します（${validItems.length}件の重複をスキップ）`)
            return
          }

          // ID重複防止のため新しいIDを生成
          const newItems: SavedItem[] = processedItems.map((item, index) => ({
            ...item,
            id: `${Date.now() + index}_${Math.random().toString(36).slice(2, 11)}`,
            createdAt: item.createdAt || new Date().toISOString(),
            updatedAt: new Date().toISOString()
          }))

          set(state => {
            let items = [...newItems, ...state.items]
            if (items.length > MAX_ITEMS) {
              items = items.slice(0, MAX_ITEMS)
            }
            return { items }
          })
          console.log('[ItemStore] set完了')
          
          const skippedCount = validItems.length - uniqueItems.length
          if (uniqueItems.length > 0) {
            const message = `${uniqueItems.length}件のアイテムをインポートしました。${skippedCount > 0 ? `（${skippedCount}件の重複をスキップ）` : ''}`
            console.log('[ItemStore] アラート表示:', message)
            alert(message)
          }
          console.log(`[ItemStore] Imported ${uniqueItems.length} unique items (${skippedCount} duplicates skipped)`)

        } catch (error) {
          console.error('[ItemStore] インポートエラー:', error)
          console.error('[ItemStore] エラースタック:', (error as Error).stack)
          alert('インポートに失敗しました。JSONフォーマットを確認してください。')
          throw error
        }
      },

      clearAll: () => {
        if (confirm('すべてのアイテムを削除しますか？この操作は取り消せません。')) {
          set({ items: [] })
        }
      },
      
      clearItems: () => {
        set({ items: [] })
      }
    }),
    {
      name: 'item-storage',
      version: 1,
      storage: createIndexedDBStorage('item-storage'),
      partialize: (state) => ({ items: state.items }),
      migrate: (persistedState: any, version: number) => {
        // バージョン0（初期状態）からバージョン1への移行
        if (version === 0) {
          // 古いデータ構造から新しいデータ構造への変換
          return persistedState
        }
        return persistedState
      }
    }
  )
)